import { Box, Button, Divider, Flex, Text, Textarea, VStack } from '@chakra-ui/react'
import { useState } from 'react'

import { formatCurrencyValue, formatDate, useAppToast } from '../helpers'
import apiService from '../services/Api'
import { IApplicantBankDetails, IPayoutInfo } from '../services/Contests/interfaces'
import { CopyButton } from './ActionButton'
import { bankLabel } from './BankSelect'
import { ConfirmDialog } from './ConfirmDialog'
import { DetailField } from './DetailField'
import PaymentDateModal from './PaymentDateModal'
import PickupInfoModal from './PickupInfoModal'
import { Tooltip } from './Tooltip'

interface IPayoutModalProps {
  isOpen: boolean
  onClose: () => void
  artId: number
  /** Called after a successful mark-paid so the parent can refetch */
  onPaymentMarked?: () => void
}

/**
 * Modal for refinement #6 — shows the same pickup info as PickupInfoModal
 * plus the payout block (sale value, commission, net value) and a
 * "Marcar como pago" button.
 *
 * Reuse strategy: composes PickupInfoModal via the `extraContent` /
 * `extraFooterActions` render-prop slots — avoids duplicating the
 * fetch/loading logic.
 */
function todayAsDateString() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

interface ICopyableValueProps {
  value: string
  entity: string
  feminine?: boolean
}

/** Value with a copy button, for the fields the transfer is keyed on. */
function CopyableValue({ value, entity, feminine }: ICopyableValueProps) {
  return (
    <Flex as="span" align="center" justify="flex-end" gap={1}>
      {value}
      <CopyButton value={value} entity={entity} feminine={feminine} description={value} size="xs" mr={0} />
    </Flex>
  )
}

/** Account the author entered at artwork submission — where the net value is transferred. */
function StudentBankDetails({ bankDetails }: { bankDetails: IApplicantBankDetails | null }) {
  if (!bankDetails) {
    return (
      <Box bg="yellow.50" borderLeft="4px solid" borderColor="yellow.400" p={2} mb={3} borderRadius="md">
        <Text fontSize="xs" color="yellow.800">
          O aluno não cadastrou dados bancários no envio das obras.
        </Text>
      </Box>
    )
  }

  const { holder_name, cpf_cnpj, bank_code, bank_name, agency, account, pix_key } = bankDetails

  return (
    <Box bg="gray.50" borderRadius="md" p={3} mb={3}>
      <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={2}>
        Dados bancários do aluno
      </Text>
      <VStack align="stretch" spacing={1}>
        <DetailField layout="inline" label="Titular" value={holder_name} />
        <DetailField
          layout="inline"
          label="CPF / CNPJ"
          value={cpf_cnpj && <CopyableValue value={cpf_cnpj} entity="CPF/CNPJ" />}
        />
        <DetailField layout="inline" label="Banco" value={bankLabel(bank_code, bank_name) || null} />
        <DetailField layout="inline" label="Agência" value={agency} />
        <DetailField layout="inline" label="Conta" value={account} />
        <DetailField
          layout="inline"
          label="Chave PIX"
          value={pix_key && <CopyableValue value={pix_key} entity="chave PIX" feminine />}
        />
      </VStack>
    </Box>
  )
}

export default function PayoutModal({ isOpen, onClose, artId, onPaymentMarked }: IPayoutModalProps) {
  const toast = useAppToast()
  const [paymentNotes, setPaymentNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedPaymentDate, setSelectedPaymentDate] = useState(todayAsDateString())
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [dateToConfirm, setDateToConfirm] = useState<string | null>(null)

  const handleClose = () => {
    setPaymentNotes('')
    setSelectedPaymentDate(todayAsDateString())
    onClose()
  }

  const handleMarkPaid = () => {
    // Open the date picker modal instead of directly marking as paid
    setIsDatePickerOpen(true)
  }

  // O date picker escolhe a data; a confirmação vem depois, já com a data em mãos.
  const confirmPayment = (date: string) => setDateToConfirm(date)

  const handleConfirmPayment = async () => {
    const date = dateToConfirm
    if (!date) {
      return
    }

    setDateToConfirm(null)
    setIsSubmitting(true)
    try {
      await apiService.patch(`/arts/${artId}/mark-paid`, {
        paid_at: date,
        payment_notes: paymentNotes.trim() || null
      })
      toast.success('Pagamento registrado', `O pagamento ao aluno foi marcado com sucesso em ${date}.`)
      handleClose()
      onPaymentMarked?.()
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { message?: string } } }
      toast.error('Erro ao registrar pagamento', apiErr)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDatePickerConfirm = (date: string) => {
    setSelectedPaymentDate(date)
    setIsDatePickerOpen(false)
    confirmPayment(date)
  }

  const renderPayoutBlock = (info: IPayoutInfo) => {
    if (!info.payout || info.art.sale_status !== 'sold') return null

    const {
      value_paid,
      commission_type,
      commission_value,
      commission,
      value_net,
      paid_at,
      paid_by,
      payment_notes: existingNotes
    } = info.payout
    const commissionLabel =
      commission_type === 'fixed' ? `R$ ${formatCurrencyValue(commission_value)}` : `${commission_value}%`
    const alreadyPaid = !!paid_at

    return (
      <>
        <Divider my={4} />

        <Box>
          <Text fontSize="sm" fontWeight="bold" mb={3} color="gray.700">
            Pagamento ao Aluno
          </Text>

          {/* Payout values */}
          <Box bg="gray.50" borderRadius="md" p={3} mb={3}>
            <Flex justify="space-between" mb={2}>
              <Text variant="caption">Valor de venda:</Text>
              <Text fontSize="xs" fontWeight="medium">
                R$ {formatCurrencyValue(value_paid)}
              </Text>
            </Flex>
            <Flex justify="space-between" mb={2}>
              <Text variant="caption">Comissão ({commissionLabel}):</Text>
              <Text fontSize="xs" fontWeight="medium" color="red.500">
                - R$ {formatCurrencyValue(commission)}
              </Text>
            </Flex>
            <Divider my={2} />
            <Flex justify="space-between">
              <Text fontSize="xs" fontWeight="bold" color="gray.700">
                Valor a receber pelo aluno:
              </Text>
              <Text fontSize="sm" fontWeight="extrabold" color="green.600">
                R$ {formatCurrencyValue(value_net)}
              </Text>
            </Flex>
          </Box>

          <StudentBankDetails bankDetails={info.applicant?.bank_details ?? null} />

          {/* Payment status */}
          <Box mb={3}>
            {alreadyPaid ? (
              <Flex align="center" gap={2}>
                <Box w="8px" h="8px" borderRadius="full" bg="green.400" flexShrink={0} />
                <Text fontSize="xs" color="green.700">
                  Pago em {formatDate(paid_at!)}
                  {paid_by ? ` por ${paid_by}` : ''}
                </Text>
              </Flex>
            ) : (
              <Flex align="center" gap={2}>
                <Box w="8px" h="8px" borderRadius="full" bg="yellow.400" flexShrink={0} />
                <Text fontSize="xs" color="yellow.700">
                  Pendente
                </Text>
              </Flex>
            )}
          </Box>

          {/* Existing notes (if already paid) */}
          {alreadyPaid && existingNotes && (
            <Box bg="gray.50" borderRadius="md" p={2} mb={3}>
              <Text variant="caption" fontStyle="italic">
                Obs: {existingNotes}
              </Text>
            </Box>
          )}

          {/* Payment notes (only when not yet paid) */}
          {!alreadyPaid && (
            <Textarea
              placeholder="Observações do pagamento (opcional): referência de transferência, PIX, etc."
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              size="sm"
              rows={2}
              borderRadius="md"
              fontSize="xs"
              resize="vertical"
            />
          )}
        </Box>
      </>
    )
  }

  const renderFooterActions = (info: IPayoutInfo) => {
    if (!info.payout || info.art.sale_status !== 'sold') return null

    const alreadyPaid = !!info.payout.paid_at
    const buyerIncomplete = !info.buyer_data_complete

    const isDisabled = alreadyPaid || buyerIncomplete

    let tooltipLabel = ''
    if (alreadyPaid) tooltipLabel = 'Este pagamento já foi registrado.'
    else if (buyerIncomplete) tooltipLabel = 'Dados do comprador incompletos. Coletar CPF/telefone no check-out antes.'

    return (
      <Tooltip label={tooltipLabel} isDisabled={!isDisabled}>
        <Box>
          <Button
            colorScheme="green"
            size="sm"
            onClick={handleMarkPaid}
            isDisabled={isDisabled}
            isLoading={isSubmitting}
            loadingText="Registrando..."
          >
            Marcar como pago
          </Button>
        </Box>
      </Tooltip>
    )
  }

  return (
    <>
      <PickupInfoModal
        isOpen={isOpen}
        onClose={handleClose}
        artId={artId}
        extraContent={renderPayoutBlock}
        extraFooterActions={renderFooterActions}
      />

      <PaymentDateModal
        isOpen={isDatePickerOpen}
        onClose={() => setIsDatePickerOpen(false)}
        onConfirm={handleDatePickerConfirm}
        defaultDate={selectedPaymentDate}
        isLoading={isSubmitting}
      />

      <ConfirmDialog
        isOpen={dateToConfirm !== null}
        onClose={() => setDateToConfirm(null)}
        onConfirm={handleConfirmPayment}
        action="save"
        title="Registrar pagamento ao aluno?"
        description={`O pagamento será marcado como feito em ${dateToConfirm ?? ''}.`}
        confirmLabel="Registrar pagamento"
        isLoading={isSubmitting}
      />
    </>
  )
}
